/**
 * =============================================================================
 * World Generator - Blueprint macro → MapData real (determinista)
 * =============================================================================
 *
 * Convierte un `WorldBlueprint` (la grilla macro que pinta Dalmiro en el World
 * Builder) en un `MapData` con los gráficos que ya tenemos cargados:
 *
 *  - Suelo por bioma con autotiling 9-slice (bloques 4×4: grass=pattern1, sand=pattern3, snow=pattern4).
 *  - Agua per-tile (water_1).
 *  - Caminos auto-conectados entre celdas path (pattern2).
 *  - Vegetación (árboles) esparcida por densidad/tipo, con colisión.
 *  - Ciudades peladas (solo suelo).
 *  - Entradas de dungeon como translados (stub a configurar).
 *
 * TODO lo aleatorio sale del PRNG seedeado con `bp.seed` → mismo blueprint +
 * misma seed ⇒ mismo mapa (determinismo).
 *
 * Convención de coordenadas: los tiles de suelo son bloques 4×4 colocados a
 * paso de 4 tiles (igual que map1.json); el renderer (GameScene /
 * MapBackgroundRenderer) los centra. Árboles y agua son per-tile.
 */

import type { MapData, MapLayer, TileData, MapTransladoData, MapParticleData, MapLightData, MapAreaData } from '../types/map';
import type { WorldBlueprint, Biome } from '../types/worldBlueprint';
import { cellIndex, VEGETATION_MIXED } from '../types/worldBlueprint';
import { createRng, hash2 } from './prng';
import { path9Sprite, blob9Pieces } from './pathSprites';
import {
  getSector,
  type SectorOutput,
  snapToBlock,
  buildCobbleConnection,
  computeCobbleRoadSet,
  sliceRoadTiles,
  findNearestPath,
} from './sectors';

/** Lado de un bloque de superficie en tiles. */
const BLOCK = 4;

/** Semi-ancho del camino del builder: 2 → 5 tiles de ancho (igual que la ciudad). */
const BUILDER_ROAD_HALF_WIDTH = 2;

// ─── Entradas de dungeon genéricas (herramienta "dungeon" del builder) ─────────
// La entrada se planta en una posición ALEATORIA (seeded) dentro de la celda y, si
// hay un camino del builder pegado, se UNE con adoquín reusando la maquinaria del
// sector de catacumbas (buildCobbleConnection). Ver docs/WORLD_BUILDER.md.
/** Radio (tiles) de la plazoleta de adoquín bajo la entrada. */
const GEN_ENTRANCE_PLAZA_RADIUS = 2;
/** Semi-ancho del camino de conexión (2 → 5 tiles de ancho, igual que el builder). */
const GEN_ENTRANCE_ROAD_HALF_WIDTH = 2;
/** Tiles libres alrededor de la entrada sin vegetación. */
const GEN_ENTRANCE_CLEAR_RADIUS = 2;
/** Margen (tiles) al borde de la celda para el offset aleatorio. */
const GEN_ENTRANCE_EDGE_MARGIN = 4;
/** Salt para derivar la sub-seed de posición de la entrada (estable por celda). */
const ENTRANCE_POS_SALT = 0xd0e7;
/** Reintentos deterministas para evitar plantar la entrada sobre agua. */
const GEN_ENTRANCE_WATER_RETRIES = 6;

// ─── Muelles (herramienta "dock" del builder) ──────────────────────────────────
// El usuario pinta el muelle sobre una celda de AGUA; el generador construye la
// pasarela en la unión con la costa de tierra más cercana + el área `embarkable`.
// Ver docs/WATER_AND_BOATS.md §Muelles.
/**
 * Pattern del piso del muelle: 13 piezas 1×1 con el MISMO layout que pattern2
 * (blob9Pieces). PLACEHOLDER adoquín hasta indexar el pattern de madera en
 * assets-index.json — después es cambiar solo esta constante y regenerar.
 */
const DOCK_PATTERN_ID = 'pattern2';
/** Semi-ancho de la pasarela: 2 → 5 tiles de ancho (o de alto si es horizontal). */
const DOCK_HALF_WIDTH = 2;
/** Largo de la pasarela (tiles, desde el nacimiento en tierra), clamp. */
const DOCK_MIN_LENGTH = 4;
const DOCK_MAX_LENGTH = 10;
/** Margen de agua alrededor de la pasarela incluido en el área embarkable. */
const DOCK_AREA_WATER_MARGIN = 2;
/** Radio máximo de búsqueda de la costa desde el punto de agua (en bloques 4×4). */
const DOCK_COAST_SEARCH_BLOCKS = 18;
/** Radio de la plazoleta de adoquín donde el camino del builder aterriza en el muelle. */
const DOCK_PLAZA_RADIUS = 2;

/** Atlas por bioma (los 9-slice viven en estos pattern atlases). */
const BIOME_ATLAS: Record<Exclude<Biome, 'water'>, string> = {
  grass: 'pattern1',
  sand: 'pattern3',
  snow: 'pattern4',
};

/**
 * Prioridad de "lapeado" tierra-tierra: en un borde entre dos biomas land, el de
 * MAYOR número dibuja su borde difuminado ENCIMA (capa 2) del bloque del de menor
 * (que queda en centro sólido, capa 1) → efecto playa (arena sobre pasto), manto de
 * nieve, etc. El agua (0) se maneja aparte con `blockBordersWater` (su costa no usa
 * esta prioridad). El orden land es indistinto; se elige pasto < arena < nieve.
 */
const BIOME_PRIORITY: Record<Biome, number> = { water: 0, grass: 1, sand: 2, snow: 3 };

/**
 * Offsets 9-slice dentro del atlas de bioma (`gridX_gridY`). El sufijo del
 * sprite real es `_4x4`. Convención de offsets confirmada en assets-index.json:
 *   _0_0=sup-izq  _4_0=sup     _8_0=sup-der
 *   _0_4=izq      _4_4=centro  _8_4=der
 *   _0_8=inf-izq  _4_8=inf     _8_8=inf-der
 * Esquinas internas (cóncavas): la celda lleva una "mordida" transparente en UNA
 * esquina (las otras 3 son pasto sólido). La mordida debe quedar EN la esquina
 * donde está el otro bioma. Medido sobre el atlas de pasto (pattern1.png) (alpha por cuadrante):
 *   _20_8=mordida NW   _12_8=mordida NE   _20_0=mordida SW   _12_0=mordida SE
 * (OJO: la convención previa estaba diagonalmente invertida → costas cóncavas
 * con la muesca de agua del lado equivocado. Ver docs/WORLD_BUILDER.md.)
 */
const NINE = {
  tl: '0_0',
  t: '4_0',
  tr: '8_0',
  l: '0_4',
  c: '4_4',
  r: '8_4',
  bl: '0_8',
  b: '4_8',
  br: '8_8',
  innerTL: '20_8', // mordida NW
  innerTR: '12_8', // mordida NE
  innerBL: '20_0', // mordida SW
  innerBR: '12_0', // mordida SE
} as const;

const WATER_SPRITE = 'water_1';

/** Árboles indexados por defecto (tree1..15, type tree). El editor pasa la lista real. */
const DEFAULT_TREE_IDS: string[] = Array.from({ length: 15 }, (_, i) => `tree${i + 1}`);

/** Decoraciones de desierto por defecto (desert1..6, type desert). Se usan en biomas `sand`. */
const DEFAULT_DESERT_IDS: string[] = Array.from({ length: 6 }, (_, i) => `desert${i + 1}`);

/** Decoraciones de nieve por defecto (snow1..6, type snow). Se usan en biomas `snow`. */
const DEFAULT_SNOW_IDS: string[] = Array.from({ length: 6 }, (_, i) => `snow${i + 1}`);

/** Entrada de dungeon por defecto si la celda no especifica una. */
const DEFAULT_ENTRANCE_IDS: string[] = ['entrance1'];

/** Cantidad de especies de árbol distintas que se usan en todo el mundo. */
const TREE_PALETTE_SIZE = 5;

/** Cantidad de árboles por celda según densidad (sobre celdas de 35×35 = 1225 tiles). */
const DENSITY_TREES: Record<string, number> = {
  none: 0,
  low: 10,
  med: 30,
  high: 60,
};

export interface GenerateOptions {
  /** IDs de árboles disponibles (de assets-index, type tree). Default tree1..15. Bioma `grass`. */
  treeIds?: string[];
  /** IDs de decoraciones de desierto (type desert). Default desert1..6. Bioma `sand`. */
  desertIds?: string[];
  /** IDs de decoraciones de nieve (type snow). Default snow1..6. Bioma `snow`. */
  snowIds?: string[];
  /** IDs de entradas de dungeon disponibles. Default entrance1. */
  entranceIds?: string[];
  /** id del MapData resultante. Default '1'. */
  mapId?: string;
  /** name del MapData resultante. Default bp.name. */
  mapName?: string;
}

/** Elige n elementos distintos de ids de forma determinista con el rng dado. */
function pickPalette(ids: readonly string[], rng: ReturnType<typeof createRng>, n: number): string[] {
  const pool = [...ids];
  const out: string[] = [];
  for (let i = 0; i < n && pool.length > 0; i++) {
    const idx = rng.int(0, pool.length - 1);
    out.push(pool.splice(idx, 1)[0]);
  }
  return out;
}

/** Devuelve el bioma de la celda que contiene el tile (tx, ty). */
/**
 * Domain warp de bordes de bioma/costa: se desplaza el muestreo del bioma con
 * ruido suave para que las costas no sean rectas. DOS octavas:
 *   - principal (AMP/FREQ): bahías y cabos GRANDES (amplitud alta, frecuencia baja),
 *     pensados para sobrevivir a la limpieza (que borra features < 2 bloques = 8 tiles).
 *   - detalle (AMP2/FREQ2): perturbación más fina que rompe el aspecto de onda regular
 *     (corre el borde grande unos tiles de forma irregular).
 * La suma de amplitudes (AMP+AMP2) se mantiene ≤ cellSize/2 para no muestrear nunca
 * más allá de la celda vecina (no "teletransporta" biomas lejanos).
 * Subir AMP → cabos/bahías más profundos; bajar FREQ → más anchos (más sobreviven a la
 * limpieza); subir AMP2/FREQ2 → costa menos "sinusoidal", más recortada.
 */
const BORDER_WARP_AMP = 11;
const BORDER_WARP_FREQ = 0.045;
const BORDER_WARP_AMP2 = 5;
const BORDER_WARP_FREQ2 = 0.12;

/** Ruido de valor suave (bilineal + smoothstep) en [0,1], determinista por seed. */
function smoothNoise(x: number, y: number, seed: number): number {
  const x0 = Math.floor(x);
  const y0 = Math.floor(y);
  const fx = x - x0;
  const fy = y - y0;
  const unit = (ix: number, iy: number): number => hash2(ix, iy, seed) / 4294967295;
  const sx = fx * fx * (3 - 2 * fx);
  const sy = fy * fy * (3 - 2 * fy);
  const v00 = unit(x0, y0);
  const v10 = unit(x0 + 1, y0);
  const v01 = unit(x0, y0 + 1);
  const v11 = unit(x0 + 1, y0 + 1);
  const a = v00 + (v10 - v00) * sx;
  const b = v01 + (v11 - v01) * sx;
  return a + (b - a) * sy;
}

/**
 * Desplazamiento de domain warp (en tiles) para un eje, sumando las dos octavas.
 * `seed` desacopla el eje X del Y (se le pasa un seed distinto a cada uno).
 */
function warpOffset(tx: number, ty: number, seed: number): number {
  const big =
    (smoothNoise(tx * BORDER_WARP_FREQ, ty * BORDER_WARP_FREQ, seed) * 2 - 1) * BORDER_WARP_AMP;
  const fine =
    (smoothNoise(tx * BORDER_WARP_FREQ2, ty * BORDER_WARP_FREQ2, (seed ^ 0x68bc) >>> 0) * 2 - 1) *
    BORDER_WARP_AMP2;
  return big + fine;
}

/**
 * Bioma del tile (tx,ty). El muestreo se desplaza con ruido suave (domain warping,
 * 2 octavas) para que los bordes de bioma y las costas no sean perfectamente rectos.
 */
function biomeAtTile(bp: WorldBlueprint, tx: number, ty: number): Biome {
  const wx = tx + warpOffset(tx, ty, bp.seed);
  const wy = ty + warpOffset(tx, ty, (bp.seed ^ 0x9e37) >>> 0);
  const cx = Math.max(0, Math.min(bp.cols - 1, Math.floor(wx / bp.cellSize)));
  const cy = Math.max(0, Math.min(bp.rows - 1, Math.floor(wy / bp.cellSize)));
  return bp.cells[cellIndex(cx, cy, bp.cols)].biome;
}

/** Predicado de vecindad: ¿el vecino en (dx,dy) "difiere" para esta pieza? */
type DiffFn = (dx: number, dy: number) => boolean;

/**
 * Offset 9-slice estándar: pieza SÓLIDA del lado de self y filo difuminado hacia los
 * vecinos que `diff` marca distintos. Devuelve una clave de `NINE`. (Se usa para la
 * costa de agua: self=tierra, diff=agua.)
 */
function edgeOffset(diff: DiffFn): string {
  const n = diff(0, -1);
  const s = diff(0, 1);
  const e = diff(1, 0);
  const w = diff(-1, 0);

  // Esquinas convexas (dos bordes orto adyacentes).
  if (n && w) return NINE.tl;
  if (n && e) return NINE.tr;
  if (s && w) return NINE.bl;
  if (s && e) return NINE.br;
  // Bordes (un borde orto).
  if (n) return NINE.t;
  if (s) return NINE.b;
  if (w) return NINE.l;
  if (e) return NINE.r;
  // Interior: chequear esquinas cóncavas (diagonal distinta).
  if (diff(-1, -1)) return NINE.innerTL;
  if (diff(1, -1)) return NINE.innerTR;
  if (diff(-1, 1)) return NINE.innerBL;
  if (diff(1, 1)) return NINE.innerBR;
  return NINE.c;
}

/**
 * Complemento de ALPHA de cada pieza 9-slice (sólido ↔ transparente). El overlay del
 * bioma alto rellena EXACTAMENTE donde el bajo se difumina, así que su pieza es el
 * complemento del borde del bajo:
 *   - bordes rectos se invierten (t↔b, l↔r),
 *   - una esquina CONVEXA (sólida 1/4) ↔ su esquina cóncava/mordida (sólida 3/4),
 *   - el centro `c` (sólido total) no tiene complemento dibujable (todo transparente).
 */
const NINE_COMPLEMENT: Record<string, string> = {
  [NINE.t]: NINE.b,
  [NINE.b]: NINE.t,
  [NINE.l]: NINE.r,
  [NINE.r]: NINE.l,
  [NINE.tl]: NINE.innerBR,
  [NINE.innerBR]: NINE.tl,
  [NINE.tr]: NINE.innerBL,
  [NINE.innerBL]: NINE.tr,
  [NINE.bl]: NINE.innerTR,
  [NINE.innerTR]: NINE.bl,
  [NINE.br]: NINE.innerTL,
  [NINE.innerTL]: NINE.br,
};

/**
 * Offset de OVERLAY: la pieza del bioma que lapea, SÓLIDA hacia su lado y difuminada
 * hacia el interior del bloque bajo. Se computa como el `edgeOffset` tratando a la
 * región del bioma alto como "lo distinto" (da la pieza sólida del lado BAJO) y luego
 * tomando su complemento de alpha → queda sólida del lado ALTO. Devuelve `NINE.c`
 * (= no dibujar) si no hay complemento (el bloque estaría todo cubierto).
 */
function overlayOffset(isH: DiffFn): string {
  return NINE_COMPLEMENT[edgeOffset(isH)] ?? NINE.c;
}

/**
 * Genera el MapData a partir del blueprint. Determinista.
 */
export function generateMapFromBlueprint(
  bp: WorldBlueprint,
  options: GenerateOptions = {}
): MapData {
  const treeIds = options.treeIds && options.treeIds.length > 0 ? options.treeIds : DEFAULT_TREE_IDS;
  const desertIds =
    options.desertIds && options.desertIds.length > 0 ? options.desertIds : DEFAULT_DESERT_IDS;
  const snowIds =
    options.snowIds && options.snowIds.length > 0 ? options.snowIds : DEFAULT_SNOW_IDS;
  const entranceIds =
    options.entranceIds && options.entranceIds.length > 0 ? options.entranceIds : DEFAULT_ENTRANCE_IDS;
  // Bloques de superficie 4×4 que cubren el mundo. `ceil` para que un worldTiles no
  // múltiplo de 4 (ej. 1225) cubra también el último tile parcial (cae en el borde).
  const blocksPerSide = Math.ceil(bp.worldTiles / BLOCK);

  // Paleta fija de especies de árbol para todo el mundo (consistencia visual):
  // se eligen TREE_PALETTE_SIZE especies al azar (seeded) y 'mixed' usa solo esas.
  const treePalette = pickPalette(treeIds, createRng(bp.seed), TREE_PALETTE_SIZE);

  // ---- 0) Grilla de bioma por bloque (con domain warping) + limpieza ----
  // Se precomputa el bioma de cada bloque (muestreo deformado por ruido) y luego
  // se hace una pasada de limpieza que elimina protrusiones/tiras finas de 1 bloque
  // (que el 9-slice no puede representar y dan esquinas feas). Quedan los quiebres
  // de ≥2 bloques → bordes orgánicos pero con esquinas limpias.
  const blockBiome: Biome[] = new Array(blocksPerSide * blocksPerSide);
  for (let by = 0; by < blocksPerSide; by++) {
    for (let bx = 0; bx < blocksPerSide; bx++) {
      blockBiome[by * blocksPerSide + bx] = biomeAtTile(bp, bx * BLOCK, by * BLOCK);
    }
  }
  const clampB = (v: number): number => (v < 0 ? 0 : v >= blocksPerSide ? blocksPerSide - 1 : v);
  const biomeAtBlock = (bx: number, by: number): Biome =>
    blockBiome[clampB(by) * blocksPerSide + clampB(bx)];
  /** True si el tile cae sobre agua (bioma del bloque). Para que el adoquín de conexión
   * (muelles/entradas/ciudades) NO cruce ni pise el agua. */
  const isWaterTile = (tx: number, ty: number): boolean =>
    biomeAtBlock(Math.floor(tx / BLOCK), Math.floor(ty / BLOCK)) === 'water';
  // Limpieza iterada HASTA PUNTO FIJO: si los dos vecinos verticales (u
  // horizontales) son el mismo bioma y el bloque difiere → se iguala. Elimina
  // features de 1 bloque de ancho/alto Y "peninsula tips" (bloques con 3 lados
  // del otro bioma): cada pasada los reduce, pero una sola flip puede crear un
  // tip nuevo en cadena, así que un número fijo de pasadas no alcanza el punto
  // fijo (quedaban esquinas de costa sin difuminar que el 9-slice no representa).
  // La regla es contractiva (siempre achica la minoría) → converge; el cap de
  // pasadas es sólo un backstop de seguridad (mantiene el determinismo).
  const MAX_CLEANUP_PASSES = 16;
  for (let pass = 0; pass < MAX_CLEANUP_PASSES; pass++) {
    const src = blockBiome.slice();
    const at = (bx: number, by: number): Biome => src[clampB(by) * blocksPerSide + clampB(bx)];
    let changed = false;
    for (let by = 0; by < blocksPerSide; by++) {
      for (let bx = 0; bx < blocksPerSide; bx++) {
        const b = src[by * blocksPerSide + bx];
        const up = at(bx, by - 1);
        const down = at(bx, by + 1);
        const left = at(bx - 1, by);
        const right = at(bx + 1, by);
        if (up === down && up !== b) {
          blockBiome[by * blocksPerSide + bx] = up;
          changed = true;
        } else if (left === right && left !== b) {
          blockBiome[by * blocksPerSide + bx] = left;
          changed = true;
        }
      }
    }
    if (!changed) break; // punto fijo alcanzado
  }

  // ---- 1) Camino como CENTERLINE a nivel de tile (mismo enfoque que
  // `buildCobbleConnection` en sectors.ts) ----
  // Cada celda `path` traza un camino RECTO por su centro (block-aligned) hacia sus
  // vecinas `path` ORTOGONALES (solo horizontal/vertical), engrosado a 5 tiles. Las
  // celdas pegadas SOLO en diagonal NO se conectan: en una grilla de tiles un camino
  // diagonal sale como escalera (jaggy), que no queremos → para doblar, el usuario pinta
  // las celdas en "L" (recto + recto). El centerline va en `pathTileSet`; el 9-slice se
  // calcula después (paso 2b).
  const isPathCell = (cx: number, cy: number): boolean =>
    cx >= 0 &&
    cy >= 0 &&
    cx < bp.cols &&
    cy < bp.rows &&
    bp.cells[cellIndex(cx, cy, bp.cols)].path;

  /** Centro (en tiles, snapeado al bloque) de la celda en el eje dado. */
  const cellCenter = (c: number): number =>
    snapToBlock(c * bp.cellSize + Math.floor(bp.cellSize / 2));

  const pathTileSet = new Set<string>();
  const hw = BUILDER_ROAD_HALF_WIDTH;
  const stampPath = (lx: number, ly: number): void => {
    for (let oy = -hw; oy <= hw; oy++) {
      for (let ox = -hw; ox <= hw; ox++) {
        const tx = lx + ox;
        const ty = ly + oy;
        if (tx < 0 || ty < 0) continue;
        pathTileSet.add(`${tx},${ty}`);
      }
    }
  };
  /** Línea entera (DDA, determinista) entre dos centros, engrosada por `stampPath`. */
  const stampLine = (x0: number, y0: number, x1: number, y1: number): void => {
    const dx = x1 - x0;
    const dy = y1 - y0;
    const steps = Math.max(Math.abs(dx), Math.abs(dy));
    if (steps === 0) {
      stampPath(x0, y0);
      return;
    }
    for (let i = 0; i <= steps; i++) {
      stampPath(Math.round(x0 + (dx * i) / steps), Math.round(y0 + (dy * i) / steps));
    }
  };
  // Solo el "medio juego" de direcciones ORTOGONALES (E, S) → cada arista recta se
  // dibuja una vez. Sin diagonales: evita las escaleras.
  const FORWARD_NEIGHBORS: [number, number][] = [
    [1, 0],
    [0, 1],
  ];
  for (let cy = 0; cy < bp.rows; cy++) {
    for (let cx = 0; cx < bp.cols; cx++) {
      if (!isPathCell(cx, cy)) continue;
      const x0 = cellCenter(cx);
      const y0 = cellCenter(cy);
      stampPath(x0, y0); // nodos sueltos se ven igual
      for (const [ndx, ndy] of FORWARD_NEIGHBORS) {
        if (!isPathCell(cx + ndx, cy + ndy)) continue;
        stampLine(x0, y0, cellCenter(cx + ndx), cellCenter(cy + ndy));
      }
    }
  }

  /** Set de tiles ocupados por camino (para suprimir vegetación). */
  const pathTiles = new Set<string>();

  /** True si algún vecino (8-dir) del bloque es agua (grilla limpia). */
  const blockBordersWater = (bx: number, by: number): boolean => {
    for (let dy = -1; dy <= 1; dy++) {
      for (let dx = -1; dx <= 1; dx++) {
        if (dx === 0 && dy === 0) continue;
        if (biomeAtBlock(bx + dx, by + dy) === 'water') return true;
      }
    }
    return false;
  };

  /** Empuja un bloque de agua per-tile (water_1 es 1×1) a la capa dada. */
  const pushWaterBlock = (tiles: TileData[], tx: number, ty: number): void => {
    for (let dy = 0; dy < BLOCK; dy++) {
      for (let dx = 0; dx < BLOCK; dx++) {
        tiles.push({ x: tx + dx, y: ty + dy, sprite: WATER_SPRITE });
      }
    }
  };

  /**
   * Bioma LAND de mayor prioridad entre los 8 vecinos que supere a `self` (o null si
   * ninguno). Ese bioma lapea su borde sobre este bloque (efecto playa). El agua no
   * cuenta (su costa se maneja con `blockBordersWater`).
   */
  type LandBiome = Exclude<Biome, 'water'>;
  const highestHigherLandNeighbor = (
    bx: number,
    by: number,
    self: Biome
  ): LandBiome | null => {
    let best: LandBiome | null = null;
    for (let dy = -1; dy <= 1; dy++) {
      for (let dx = -1; dx <= 1; dx++) {
        if (dx === 0 && dy === 0) continue;
        const nb = biomeAtBlock(bx + dx, by + dy);
        if (nb === 'water' || BIOME_PRIORITY[nb] <= BIOME_PRIORITY[self]) continue;
        if (best === null || BIOME_PRIORITY[nb] > BIOME_PRIORITY[best]) best = nb;
      }
    }
    return best;
  };

  // ---- 2) Suelo en dos capas (modelo "borde lapeado"):
  //   - layer1_ground (z0, atrás): el SUELO. Agua, o el RELLENO sólido (centro) del
  //     bioma del bloque. Bajo una costa de agua va AGUA para que el difuminado no
  //     quede negro.
  //   - layer2_floor_objects (z1, encima del agua y debajo del personaje): los bordes
  //     que tienen filo difuminado y van ENCIMA:
  //       · costa de agua (el bioma del bloque lapeando sobre el agua), y
  //       · overlay del bioma land VECINO de MAYOR prioridad lapeando sobre este bloque
  //         (arena sobre pasto = playa, nieve sobre arena/pasto, etc.).
  //   Los bordes tierra-tierra simétricos viejos (cada bloque difuminaba hacia su vecino
  //   revelando su propio centro = línea dura) se reemplazan por este overlay: el bloque
  //   BAJO queda en centro sólido (capa 1) y el ALTO le lapea su filo encima (capa 2).
  const groundTiles: TileData[] = [];
  const coastTiles: TileData[] = [];
  for (let by = 0; by < blocksPerSide; by++) {
    for (let bx = 0; bx < blocksPerSide; bx++) {
      const tx = bx * BLOCK;
      const ty = by * BLOCK;

      // El terreno se dibuja SIEMPRE (también debajo del camino, para que el
      // adoquín difumine sobre él en capa 2).
      const biome = biomeAtBlock(bx, by);
      if (biome === 'water') {
        pushWaterBlock(groundTiles, tx, ty);
        continue;
      }

      const atlas = BIOME_ATLAS[biome];
      const centerSprite = `${atlas}_${NINE.c}_4x4`;
      const bordersWater = blockBordersWater(bx, by);

      // Capa 1: relleno base. Agua bajo la costa (difuminado), o centro sólido del bioma.
      if (bordersWater) {
        pushWaterBlock(groundTiles, tx, ty);
      } else {
        groundTiles.push({ x: tx, y: ty, sprite: centerSprite });
      }

      // Capa 2a: costa de AGUA (este bioma lapea sobre el agua), igual que antes.
      if (bordersWater) {
        const off = edgeOffset((dx, dy) => biomeAtBlock(bx + dx, by + dy) === 'water');
        if (off !== NINE.c) coastTiles.push({ x: tx, y: ty, sprite: `${atlas}_${off}_4x4` });
      }

      // Capa 2b: overlay del vecino land de mayor prioridad lapeando sobre este bloque.
      // Se agrega DESPUÉS de la costa de agua → queda por encima en capa 2.
      const high = highestHigherLandNeighbor(bx, by, biome);
      if (high) {
        const off = overlayOffset((dx, dy) => {
          const nb = biomeAtBlock(bx + dx, by + dy);
          return nb !== 'water' && BIOME_PRIORITY[nb] >= BIOME_PRIORITY[high];
        });
        if (off !== NINE.c) {
          coastTiles.push({ x: tx, y: ty, sprite: `${BIOME_ATLAS[high]}_${off}_4x4` });
        }
      }
    }
  }

  // ---- 2b) Geometría del camino del builder ----
  // Acá SOLO marcamos los tiles del camino del builder (`pathTiles`) para: (a) suprimir
  // vegetación encima, y (b) que los sectores (ciudades/entradas) los vean como camino al
  // conectarse. El 9-slice del builder se EMITE más abajo (paso 2e), una vez resueltos los
  // sectores, para que los tiles del borde con el adoquín de la ciudad/entrada se calculen
  // contra ese adoquín y empalmen en CENTRO sólido en vez de un borde difuminado
  // ("falta 1 tile para unir").
  for (const key of pathTileSet) pathTiles.add(key);

  // Decoración (árboles + entradas) y translados: se declaran acá porque las
  // entradas de dungeon (paso 2d) se generan ANTES de la vegetación (para suprimir
  // árboles sobre su adoquín) pero comparten estos arrays con la veg y los sectores.
  const decoTiles: TileData[] = [];
  // Techos de casa (capa `roof`, siempre encima del personaje) y colisión de huella
  // (capa `_collision`, no se renderiza) — los aportan los sectores de ciudad.
  const roofTiles: TileData[] = [];
  const blockedTiles: TileData[] = [];
  const translados: MapTransladoData[] = [];

  // ---- 2c) Sectores custom: resolver placements ANTES de la vegetación ----
  // Cada sector aporta deco/translados/(ground) y un set de tiles donde la
  // vegetación genérica NO se debe esparcir (sectorSuppress). Ver world/sectors.ts.
  const sectorOutputs: SectorOutput[] = [];
  const sectorSuppress = new Set<string>();
  // Tiles de adoquín (pattern2) aportados por sectores/entradas: el 9-slice diferido del
  // builder (2e) los trata como camino para empalmar sin borde difuminado.
  const sectorRoadTiles = new Set<string>();
  if (bp.sectors) {
    for (const placement of bp.sectors) {
      const def = getSector(placement.sectorId);
      if (!def) continue;
      const centerTileX = placement.cellX * bp.cellSize + Math.floor(bp.cellSize / 2);
      const centerTileY = placement.cellY * bp.cellSize + Math.floor(bp.cellSize / 2);
      const radiusTiles = placement.radiusTiles ?? def.defaultRadiusTiles;
      const out = def.generate({
        centerTileX,
        centerTileY,
        radiusTiles,
        seed: hash2(placement.cellX, placement.cellY, (bp.seed ^ 0x5ec70) >>> 0),
        treeIds,
        entranceIds,
        isPath: (tx, ty) => pathTiles.has(`${tx},${ty}`),
        isWater: isWaterTile,
        mapId: Number(options.mapId ?? '1'),
      });
      sectorOutputs.push(out);
      if (out.suppressTiles) {
        for (const k of out.suppressTiles) sectorSuppress.add(k);
      }
      for (const ct of out.coastTiles ?? []) {
        if (typeof ct.sprite === 'string' && ct.sprite.startsWith('pattern2_')) {
          sectorRoadTiles.add(`${ct.x},${ct.y}`);
        }
      }
    }
  }

  // ---- 2d) Entradas de dungeon genéricas (cell.dungeonEntrance) ----
  // Antes iban SIEMPRE al centro de la celda (50/50), bloqueadas y sin conexión.
  // Ahora: posición ALEATORIA (seeded) dentro de la celda + —si hay camino del
  // builder pegado— unión con adoquín (plazoleta + "L"), reusando la maquinaria del
  // sector de catacumbas. La entrada queda PISABLE para que el translado se dispare.
  // Va ANTES de la vegetación para sumar su adoquín/claro a `sectorSuppress`.
  const isPathTileBuilder = (x: number, y: number): boolean => pathTiles.has(`${x},${y}`);
  for (let cy = 0; cy < bp.rows; cy++) {
    for (let cx = 0; cx < bp.cols; cx++) {
      const cell = bp.cells[cellIndex(cx, cy, bp.cols)];
      if (!cell.dungeonEntrance) continue;

      const cellX0 = cx * bp.cellSize;
      const cellY0 = cy * bp.cellSize;
      const posRng = createRng(hash2(cx, cy, (bp.seed ^ ENTRANCE_POS_SALT) >>> 0));
      const span = Math.max(0, bp.cellSize - 1 - 2 * GEN_ENTRANCE_EDGE_MARGIN);

      // Offset aleatorio dentro de la celda (con margen), snapeado al bloque 4×4
      // para empalmar sin escalón con el camino del builder (que es block-aligned).
      // Reintenta determinista para no caer sobre agua; si no, cae al centro.
      let ex = snapToBlock(cellX0 + Math.floor(bp.cellSize / 2));
      let ey = snapToBlock(cellY0 + Math.floor(bp.cellSize / 2));
      for (let attempt = 0; attempt <= GEN_ENTRANCE_WATER_RETRIES; attempt++) {
        const tx = snapToBlock(cellX0 + GEN_ENTRANCE_EDGE_MARGIN + posRng.int(0, span));
        const ty = snapToBlock(cellY0 + GEN_ENTRANCE_EDGE_MARGIN + posRng.int(0, span));
        if (biomeAtBlock(Math.floor(tx / BLOCK), Math.floor(ty / BLOCK)) !== 'water') {
          ex = tx;
          ey = ty;
          break;
        }
      }

      // Sprite de entrada: el elegido en el blueprint, o uno seedeado por celda.
      const entranceSprite =
        cell.dungeonEntranceSprite ??
        pickPalette(entranceIds, createRng(hash2(cx, cy, bp.seed ^ 0xd0e)), 1)[0] ??
        entranceIds[0];

      // Conexión de adoquín SOLO si hay un camino del builder pegado (dentro de ~media
      // celda). Si lo hay, plazoleta bajo la entrada + "L" a cada camino por octante.
      const maxConnect = Math.ceil(bp.cellSize / 2);
      if (findNearestPath(isPathTileBuilder, ex, ey, maxConnect, isWaterTile) !== null) {
        const cobble = buildCobbleConnection(ex, ey, isPathTileBuilder, {
          plazaRadius: GEN_ENTRANCE_PLAZA_RADIUS,
          halfWidth: GEN_ENTRANCE_ROAD_HALF_WIDTH,
          maxConnect,
          isBlocked: isWaterTile,
        });
        coastTiles.push(...cobble.coastTiles);
        for (const key of cobble.tiles) {
          sectorSuppress.add(key);
          sectorRoadTiles.add(key);
        }
      }

      // Claro de vegetación alrededor de la entrada (conecte o no).
      for (let dy = -GEN_ENTRANCE_CLEAR_RADIUS; dy <= GEN_ENTRANCE_CLEAR_RADIUS; dy++) {
        for (let dx = -GEN_ENTRANCE_CLEAR_RADIUS; dx <= GEN_ENTRANCE_CLEAR_RADIUS; dx++) {
          if (dx * dx + dy * dy > GEN_ENTRANCE_CLEAR_RADIUS * GEN_ENTRANCE_CLEAR_RADIUS) continue;
          const tx = ex + dx;
          const ty = ey + dy;
          if (tx < 0 || ty < 0) continue;
          sectorSuppress.add(`${tx},${ty}`);
        }
      }

      // El visual de la entrada es ESTA decoración (ground_deco_over, ocluye como
      // estructura). PISABLE (sin `blocked`): el player la pisa y dispara el translado
      // en el mismo tile — el translado va sin sprite para no dibujarla dos veces.
      decoTiles.push({ x: ex, y: ey, sprite: entranceSprite });
      translados.push({
        id: `dungeon_${cx}_${cy}`,
        x: ex,
        y: ey,
        // Stub: configurar destino real al cablear el dungeon.
        destMapId: 0,
        destX: ex,
        destY: ey,
      });
    }
  }

  // ---- 2d-bis) MUELLES (cell.dock) ----
  // La celda dock marca un punto SOBRE EL AGUA; acá se busca la costa de tierra
  // más cercana y se construye la pasarela en esa unión: 5 tiles de ancho, nace
  // en tierra firme y se extiende recta sobre el agua hacia el punto marcado.
  // El piso es 9-slice a nivel de tile (DOCK_PATTERN_ID, capa 2): sus piezas
  // `_1x1` marcan tierra POR TILE en el índice de agua (waterUtils) → la
  // pasarela es caminable y el agua pegada sigue navegable. Además se emite el
  // área `embarkable` con margen de agua (solo ahí se bota/desembarca el
  // barco). Va ANTES de la vegetación para sumar su claro a sectorSuppress.
  const dockAreas: MapAreaData[] = [];
  const DOCK9 = blob9Pieces(DOCK_PATTERN_ID);
  for (let cy = 0; cy < bp.rows; cy++) {
    for (let cx = 0; cx < bp.cols; cx++) {
      const cell = bp.cells[cellIndex(cx, cy, bp.cols)];
      if (!cell.dock) continue;

      // 1) Punto de agua W (en bloques): el bloque de agua más cercano al
      //    centro de la celda (idealmente el centro mismo).
      const cellX0 = cx * bp.cellSize;
      const cellY0 = cy * bp.cellSize;
      const centerBx = Math.floor((cellX0 + Math.floor(bp.cellSize / 2)) / BLOCK);
      const centerBy = Math.floor((cellY0 + Math.floor(bp.cellSize / 2)) / BLOCK);
      let wBx = -1;
      let wBy = -1;
      let bestD = Infinity;
      for (let by = Math.floor(cellY0 / BLOCK); by * BLOCK < cellY0 + bp.cellSize; by++) {
        for (let bx = Math.floor(cellX0 / BLOCK); bx * BLOCK < cellX0 + bp.cellSize; bx++) {
          if (biomeAtBlock(bx, by) !== 'water') continue;
          const d = Math.abs(bx - centerBx) + Math.abs(by - centerBy);
          if (d < bestD) {
            bestD = d;
            wBx = bx;
            wBy = by;
          }
        }
      }
      if (wBx < 0) {
        console.warn(`[worldGenerator] Muelle en celda (${cx},${cy}) sin agua — skip`);
        continue;
      }

      // 2) Costa más cercana: anillos Chebyshev de bloques desde W hasta el
      //    primer bloque de TIERRA (determinista por orden de escaneo).
      let lBx = -1;
      let lBy = -1;
      outer: for (let r = 1; r <= DOCK_COAST_SEARCH_BLOCKS; r++) {
        for (let dy = -r; dy <= r; dy++) {
          for (let dx = -r; dx <= r; dx++) {
            if (Math.max(Math.abs(dx), Math.abs(dy)) !== r) continue;
            const bx = wBx + dx;
            const by = wBy + dy;
            if (bx < 0 || by < 0) continue;
            if (bx * BLOCK >= bp.worldTiles || by * BLOCK >= bp.worldTiles) continue;
            if (biomeAtBlock(bx, by) !== 'water') {
              lBx = bx;
              lBy = by;
              break outer;
            }
          }
        }
      }
      if (lBx < 0) {
        console.warn(`[worldGenerator] Muelle en celda (${cx},${cy}) sin costa cerca — skip`);
        continue;
      }

      // 3) Pasarela: eje cardinal dominante tierra→agua, recta desde el centro
      //    del bloque de tierra (nace tierra adentro) hacia W, largo clampeado.
      const landX = lBx * BLOCK + 2;
      const landY = lBy * BLOCK + 2;
      const waterX = wBx * BLOCK + 2;
      const waterY = wBy * BLOCK + 2;
      const deltaX = waterX - landX;
      const deltaY = waterY - landY;
      const horizontal = Math.abs(deltaX) >= Math.abs(deltaY);
      const dirX = horizontal ? Math.sign(deltaX) || 1 : 0;
      const dirY = horizontal ? 0 : Math.sign(deltaY) || 1;
      const axisDist = horizontal ? Math.abs(deltaX) : Math.abs(deltaY);
      const length = Math.max(DOCK_MIN_LENGTH, Math.min(DOCK_MAX_LENGTH, axisDist));

      const dockTiles = new Set<string>();
      for (let t = 0; t <= length; t++) {
        const cxTile = landX + dirX * t;
        const cyTile = landY + dirY * t;
        for (let p = -DOCK_HALF_WIDTH; p <= DOCK_HALF_WIDTH; p++) {
          const tx = cxTile + (horizontal ? 0 : p);
          const ty = cyTile + (horizontal ? p : 0);
          if (tx < 0 || ty < 0 || tx >= bp.worldTiles || ty >= bp.worldTiles) continue;
          dockTiles.add(`${tx},${ty}`);
        }
      }
      if (dockTiles.size === 0) continue;

      // Piso 9-slice contra los propios tiles del muelle + claro de vegetación.
      const isDock = (x: number, y: number): boolean => dockTiles.has(`${x},${y}`);
      let minX = Infinity;
      let minY = Infinity;
      let maxX = -Infinity;
      let maxY = -Infinity;
      for (const key of dockTiles) {
        const [x, y] = key.split(',').map(Number);
        coastTiles.push({
          x,
          y,
          sprite: path9Sprite(
            isDock(x, y - 1),
            isDock(x, y + 1),
            isDock(x + 1, y),
            isDock(x - 1, y),
            isDock(x - 1, y - 1),
            isDock(x + 1, y - 1),
            isDock(x - 1, y + 1),
            isDock(x + 1, y + 1),
            DOCK9
          ),
        });
        sectorSuppress.add(key);
        if (x < minX) minX = x;
        if (y < minY) minY = y;
        if (x > maxX) maxX = x;
        if (y > maxY) maxY = y;
      }

      // Área embarkable: la pasarela + margen de agua alrededor (el tile de agua
      // del navegante debe caer adentro para poder desembarcar).
      const m = DOCK_AREA_WATER_MARGIN;
      dockAreas.push({
        id: `area_dock_${cx}_${cy}`,
        name: 'Muelle',
        embarkable: true,
        rects: [
          {
            x: Math.max(0, minX - m),
            y: Math.max(0, minY - m),
            w: maxX - minX + 1 + m * 2,
            h: maxY - minY + 1 + m * 2,
          },
        ],
      });

      // Conexión de adoquín al camino del builder: si hay un camino cerca del extremo de
      // tierra de la pasarela, tirar una "L" por cada dirección cardinal con camino (igual
      // que las entradas de dungeon, paso 2d). El ancla es la tierra firme donde NACE el
      // muelle (landX,landY = centro del bloque de costa), así el adoquín va SIEMPRE hacia
      // tierra y nunca carva el agua navegable.
      const dockMaxConnect = Math.ceil(bp.cellSize); // alcanza el centerline de la celda limítrofe
      if (findNearestPath(isPathTileBuilder, landX, landY, dockMaxConnect, isWaterTile) !== null) {
        const roadSet = computeCobbleRoadSet(landX, landY, isPathTileBuilder, {
          plazaRadius: DOCK_PLAZA_RADIUS,
          halfWidth: DOCK_HALF_WIDTH,
          maxConnect: dockMaxConnect,
          // El adoquín esquiva el agua: no cruza el lago hacia un camino del otro lado ni
          // pisa el agua navegable. Sólo conecta a un camino alcanzable por tierra.
          isBlocked: isWaterTile,
        });
        // El adoquín tampoco debe pisar las tablas del muelle: restamos los tiles de la
        // propia pasarela del set → sin doble tile en el arranque. El 9-slice igual EMPALMA
        // sin costura porque su test de vecino cuenta como camino tanto el camino del builder
        // (isPathTileBuilder) como las tablas del muelle (isDock) → los tiles de adoquín
        // pegados al muelle usan la pieza de centro, no un borde duro.
        for (const key of dockTiles) roadSet.delete(key);
        const isRoadNeighbor = (x: number, y: number): boolean =>
          isPathTileBuilder(x, y) || isDock(x, y);
        coastTiles.push(...sliceRoadTiles(roadSet, isRoadNeighbor));
        for (const key of roadSet) {
          sectorSuppress.add(key);
          sectorRoadTiles.add(key);
        }
      }
    }
  }

  // ---- 2e) 9-slice del camino del builder (DIFERIDO, en capa 2) ----
  // Ya conocemos el adoquín de los sectores (plazas/conexiones de ciudad y entradas): un
  // vecino cuenta como camino si es del builder O adoquín de sector, así el borde con la
  // ciudad/entrada queda en CENTRO sólido (empalme sin costura) en vez de un borde
  // difuminado. Se emite ANTES del flush de sectores (paso 5) → el adoquín del sector
  // queda por encima en los (pocos) tiles compartidos.
  const isRoadTile = (x: number, y: number): boolean =>
    pathTileSet.has(`${x},${y}`) || sectorRoadTiles.has(`${x},${y}`);
  for (const key of pathTileSet) {
    const [x, y] = key.split(',').map(Number);
    coastTiles.push({
      x,
      y,
      sprite: path9Sprite(
        isRoadTile(x, y - 1),
        isRoadTile(x, y + 1),
        isRoadTile(x + 1, y),
        isRoadTile(x - 1, y),
        isRoadTile(x - 1, y - 1),
        isRoadTile(x + 1, y - 1),
        isRoadTile(x - 1, y + 1),
        isRoadTile(x + 1, y + 1)
      ),
    });
  }

  // ---- 3) Vegetación/decoración por celda según densidad, POR BIOMA ----
  // Cada bioma usa su propio pool: grass → árboles, sand → decos de desierto, snow → decos
  // de nieve. Así "según la zona" aparece la decoración que corresponde (todas bloquean).
  for (let cy = 0; cy < bp.rows; cy++) {
    for (let cx = 0; cx < bp.cols; cx++) {
      const cell = bp.cells[cellIndex(cx, cy, bp.cols)];
      if (cell.city || cell.biome === 'water') continue;
      const count = DENSITY_TREES[cell.density] ?? 0;
      if (count === 0) continue;

      const rng = createRng(hash2(cx, cy, bp.seed));
      const baseTx = cx * bp.cellSize;
      const baseTy = cy * bp.cellSize;
      const used = new Set<string>();
      const speciesFixed =
        cell.vegetationType && cell.vegetationType !== VEGETATION_MIXED
          ? cell.vegetationType
          : null;

      // Pool de especies de esta celda SEGÚN el bioma:
      //   - grass: la especie fija elegida en el editor, o la paleta global de árboles.
      //   - sand:  decoraciones de desierto.
      //   - snow:  decoraciones de nieve.
      // (La especie fija del editor solo aplica a pasto; arena/nieve usan su pool.)
      let cellSpecies: string[];
      if (cell.biome === 'sand') cellSpecies = desertIds;
      else if (cell.biome === 'snow') cellSpecies = snowIds;
      else cellSpecies = speciesFixed ? [speciesFixed] : treePalette;
      if (cellSpecies.length === 0) continue; // sin pool para este bioma → nada que dispersar

      for (let i = 0; i < count; i++) {
        const tx = baseTx + rng.int(0, bp.cellSize - 1);
        const ty = baseTy + rng.int(0, bp.cellSize - 1);
        const key = `${tx},${ty}`;
        if (used.has(key) || pathTiles.has(key) || sectorSuppress.has(key)) continue;
        // No poner árboles sobre el agua de la costa ondulada (grilla limpia).
        if (biomeAtBlock(Math.floor(tx / BLOCK), Math.floor(ty / BLOCK)) === 'water') continue;
        used.add(key);
        const sprite = rng.pick(cellSpecies) ?? cellSpecies[0] ?? treeIds[0];
        decoTiles.push({ x: tx, y: ty, sprite, blocked: true });
      }
    }
  }

  // (Entradas de dungeon: generadas en el paso 2d, antes de la vegetación.)

  // ---- 5) Volcar las salidas de los sectores custom ----
  const sectorParticles: MapParticleData[] = [];
  const sectorLights: MapLightData[] = [];
  const sectorAreas: MapAreaData[] = [];
  for (const out of sectorOutputs) {
    if (out.groundTiles) groundTiles.push(...out.groundTiles);
    // Tierra (pattern6) del sendero puerta→camino: va en la MISMA capa que el adoquín pero
    // ANTES, así el adoquín (avenida/ruta) se dibuja por encima y la tierra solo se ve en el
    // pasto de los huecos.
    if (out.dirtTiles) coastTiles.push(...out.dirtTiles);
    if (out.coastTiles) coastTiles.push(...out.coastTiles);
    if (out.decoTiles) decoTiles.push(...out.decoTiles);
    if (out.roofTiles) roofTiles.push(...out.roofTiles);
    if (out.blockedTiles) blockedTiles.push(...out.blockedTiles);
    if (out.translados) translados.push(...out.translados);
    if (out.particles) sectorParticles.push(...out.particles);
    if (out.lights) sectorLights.push(...out.lights);
    if (out.areas) sectorAreas.push(...out.areas);
  }
  // Áreas de muelle (paso 2d-bis): embarkables, se regeneran frescas en cada build.
  sectorAreas.push(...dockAreas);

  const layers: MapLayer[] = [
    { name: 'layer1_ground', z: 0, tiles: groundTiles },
    // Costa: bordes/esquinas de bioma sobre el agua y debajo del personaje (depth -100).
    { name: 'layer2_floor_objects', z: 1, tiles: coastTiles },
    { name: 'ground_deco_over', z: 30, tiles: decoTiles },
  ];
  // Techos: capa `roof` (z=40, depth fijo SIEMPRE encima del personaje). Solo si hay.
  if (roofTiles.length > 0) {
    layers.push({ name: 'roof', z: 40, tiles: roofTiles });
  }
  // Colisión de huella de edificios: capa `_collision` (prefijo `_` → el cliente NO la
  // renderiza; el server lee `blocked` de todas las capas). Solo si hay.
  if (blockedTiles.length > 0) {
    layers.push({ name: '_collision', z: 100, tiles: blockedTiles });
  }

  return {
    id: options.mapId ?? '1',
    name: options.mapName ?? bp.name,
    bounds: { minX: 0, maxX: bp.worldTiles - 1, minY: 0, maxY: bp.worldTiles - 1 },
    layers,
    npcs: [],
    translados: translados.length > 0 ? translados : undefined,
    particles: sectorParticles.length > 0 ? sectorParticles : undefined,
    lights: sectorLights.length > 0 ? sectorLights : undefined,
    // Áreas de interior de casa (efecto de oscurecer al entrar). Las áreas base
    // (facción/ciudad) las suma el editor (WorldBuilderView) al preservar map1.
    areas: sectorAreas.length > 0 ? sectorAreas : undefined,
  };
}
